import { useSnackbar } from '@components/snackbar';
import {
  AssigneMenu,
  UserSelectDropDownIndicator,
  UserSelectMultiValueLabel_custom,
  UserSelectOption_custom,
  UserSelectPlaceholder_custom,
  UserSelectSingleValue_custom,
  userSelectStyles_custom,
} from '@sketchnote/shared-components';
import cn from 'classnames';
import React, { useState } from 'react';
import Select from 'react-select';
import { useBoardStore } from 'src/features/boards';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { Member } from 'src/type';

import { useTasksStore } from '../../store/useTasksStore';
import { Task } from '../../types/tasks';

type Person = {
  firstname: string;
  avatar: string;
  memberId: string;
  lastname: string;
  email: string;
};

export const PersonFieldComponent = ({
  task,
  fieldId,
  data,
  members,
  disabled,
}: {
  task: Partial<Task>;
  fieldId: string;
  label: string;
  data: any;
  members?: Member[];
  handleDelete: (flag: boolean) => void;
  disabled?: boolean;
}): JSX.Element => {
  const dataToSet = Array.isArray(data) && data.filter((item) => typeof item.memberId === 'string');
  const [selectedOptions, setSelectedOptions] = useState<any>(dataToSet);
  const [isFocused, setIsFocused] = useState(false);

  const boardsStore = useBoardStore();

  const onUpdateCustomFieldData = useTasksStore((s) => s.onUpdateCustomFieldData);
  const onUpdateCustomTemplateFieldData = useTasksStore((s) => s.onUpdateCustomTemplateFieldData);
  const { isReadOnly } = useTaskAccess(task.id || '');
  const [open] = useSnackbar();

  const [added, setAdded] = useState<any>([]);

  const assigneeOptions = React.useMemo(
    () => [
      {
        label: 'Select people',
        options:
          members?.map((obj) => ({
            label: obj.firstname,
            value: obj.memberId,
            memberId: obj.memberId,
            avatar: obj.avatar,
          })) || [],
      },
    ],
    [members]
  );

  React.useEffect(() => {
    setSelectedOptions(dataToSet);
  }, [data]);

  React.useEffect(() => {
    if (!isFocused) {
      setAdded([]);
    }
  }, [isFocused]);

  const handleUpdateFieldData = async () => {
    if (task.id) {
      const payload = {
        taskId: task.id,
        fieldId: fieldId,
        data: selectedOptions,
        added: added,
      };
      let success = false;
      if (task.board?.id === undefined) {
        payload['taskTemplateRef'] = task.id;
        success = await onUpdateCustomTemplateFieldData(payload);
      } else {
        payload['taskTemplateRef'] = undefined;
        success = await onUpdateCustomFieldData(payload);
      }
      if (!success) {
        open({
          text: 'Failed to update field data',
          snackType: 'error',
          position: 'top-center',
          duration: 3000,
        });
      }
    }
    setIsFocused(false);
  };

  const handleChange = (values) => {
    const assignee = values?.map((obj) => ({
      firstname: obj.label,
      memberId: obj.memberId,
      avatar: obj.avatar,
      lastname: obj.lastname,
    }));
    if (assignee.length > selectedOptions.length) {
      const selectedOptionMemberIds = selectedOptions.map((item) => item.memberId);
      const newItems: [] = assignee.filter(
        (item) => !selectedOptionMemberIds.includes(item.memberId)
      );
      setAdded((prev) => {
        return [...prev, ...newItems];
      });
    }
    setSelectedOptions(assignee);
  };

  return (
    <div className="flex w-400 h-36 pl-5">
      <>
        <Select
          options={assigneeOptions}
          value={
            Array.isArray(selectedOptions) &&
            selectedOptions &&
            (selectedOptions?.map((obj) => ({
              label: obj.firstname,
              value: obj.memberId,
              memberId: obj.memberId,
              avatar: obj.avatar,
            })) as any)
          }
          className={cn('text-sm text-gray-999 font-regular placeholder-gray-500 h-36 w-400 pl-2', {
            'bg-white rounded-sm border-gray-300': isFocused,
          })}
          menuPortalTarget={document.body}
          isDisabled={disabled || isReadOnly || boardsStore.isPublicView}
          placeholder={<UserSelectPlaceholder_custom labelClassName={'text-sm'} />}
          styles={userSelectStyles_custom}
          onChange={handleChange}
          isMulti
          components={{
            DropdownIndicator: isReadOnly ? null : UserSelectDropDownIndicator,
            IndicatorSeparator: () => null,
            SingleValue: UserSelectSingleValue_custom,
            Option: UserSelectOption_custom,
            MultiValueLabel: UserSelectMultiValueLabel_custom,
            Menu: AssigneMenu,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (task.id) {
                const payload = {
                  taskId: task.id,
                  fieldId: fieldId,
                  data: selectedOptions,
                  added: added,
                };
                if (task.board?.id === undefined) {
                  payload['taskTemplateRef'] = task.id;
                  onUpdateCustomTemplateFieldData(payload);
                } else {
                  payload['taskTemplateRef'] = undefined;
                  onUpdateCustomFieldData(payload);
                }
              }
              setIsFocused(false);
            }
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={handleUpdateFieldData}
        />
      </>
    </div>
  );
};
